'use client';

import { useEffect, useRef, useState } from 'react';
import Link from "next/link";
import Image from "next/image";
import { List, X } from "@/app/components/Icons/Phosphoricons";

const menuItems = [
    { name: 'IT Solutions', href: '/itsolutions' },
    { name: 'Websites', href: '/frontend' },
    { name: 'Apps', href: '/apps' },
    { name: 'Back End', href: '/backend' },
    { name: 'DevSecOps', href: '/devopsdevsecops' },
    { name: 'Pricing', href: '/pricing' },
    { name: 'Contact', href: '/contact' },
];

interface MenuItem {
    name: string;
    href: string;
}

export default function NavBar() {
    const [isClient, setIsClient] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const headerRef = useRef<HTMLElement>(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    useEffect(() => {
        setIsClient(true);
    }, []);

    const handleMenuItemClick = () => setMobileMenuOpen(false);

    const handleLogin = async () => {
        if (isLoggingIn) return;

        try {
            setIsLoggingIn(true);
            // Instead of doing this client-side, just redirect to the server-side login handler
            window.location.href = '/api/auth/login';
        } catch (error) {
            console.error('Error during login redirect:', error);
            setIsLoggingIn(false);
        }
    };

    useEffect(() => {
        if (!isClient) return;

        const updateHeaderHeight = () => {
            if (headerRef.current) {
                setHeaderHeight(headerRef.current.offsetHeight);
            }
        };

        const timer = setTimeout(updateHeaderHeight, 0);
        window.addEventListener('resize', updateHeaderHeight);

        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setMobileMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            clearTimeout(timer);
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener('resize', updateHeaderHeight);
        };
    }, [isClient]);

    if (!isClient) {
        return null;
    }

    return (
        <header ref={headerRef} className="fixed top-0 left-0 w-full z-50 bg-white shadow-md">
            <nav className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8">
                <div className="flex lg:flex-1">
                    <Link href="/" className="-m-1.5 p-1.5">
                        <span className="sr-only">FullStack Software Labs</span>
                        <div className="flex items-center">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-16 lg:h-16 relative mr-2">
                                <Image
                                    src="/vistalogos/logo-transparent-png.png"
                                    alt="FullStack Software Labs Logo"
                                    width={64}
                                    height={64}
                                    style={{ objectFit: 'contain', width: '100%', height: '100%' }}
                                    unoptimized
                                />
                            </div>
                            <div className="flex flex-col text-black text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-normal">
                                <span>FullStack</span>
                                <span>Software Labs</span>
                            </div>
                        </div>
                    </Link>
                </div>

                <div className="lg:hidden">
                    <button
                        type="button"
                        aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                        aria-expanded={mobileMenuOpen}
                        aria-controls="mobile-menu"
                        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
                    >
                        {mobileMenuOpen ? <X size={24}/> : <List size={24}/>}
                    </button>
                </div>

                {/* Desktop Menu */}
                <div className="hidden lg:flex lg:gap-x-1 flex-wrap">
                    {menuItems.map((item: MenuItem, index: number) => (
                        <div key={item.href} className="flex items-center">
                            <Link
                                href={item.href}
                                className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-normal text-gray-900 hover:text-blue-600 whitespace-nowrap"
                            >
                                {item.name}
                            </Link>
                            {index < menuItems.length - 1 && <span className="text-gray-900 mx-2">|</span>}
                        </div>
                    ))}
                </div>
                <div className="hidden lg:flex lg:flex-1 lg:justify-end">
                    <button
                        onClick={handleLogin}
                        disabled={isLoggingIn}
                        className="text-xs sm:text-sm md:text-base lg:text-lg xl:text-xl font-medium px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-75"
                    >
                        {isLoggingIn ? 'Loading...' : 'Log In'}
                    </button>
                </div>
            </nav>

            {/* Mobile Menu */}
            {isClient && (
                <div
                    ref={menuRef}
                    className={`fixed inset-0 z-40 bg-white overflow-auto transition-opacity duration-200 ${mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    style={{top: `${headerHeight}px`}}
                >
                    <div className="p-6 overflow-y-auto h-full">
                        <nav className="flex flex-col">
                            {menuItems.map((item) => (
                                <Link
                                    key={item.href}
                                    href={item.href}
                                    onClick={handleMenuItemClick}
                                    className="py-4 px-4 border-b border-gray-200 text-gray-900 hover:bg-gray-100 transition"
                                >
                                    {item.name}
                                </Link>
                            ))}
                            <button
                                onClick={handleLogin}
                                disabled={isLoggingIn}
                                className="py-4 px-4 border-b border-gray-200 bg-blue-600 text-white hover:bg-blue-700 transition mt-4 text-left disabled:opacity-75"
                            >
                                {isLoggingIn ? 'Loading...' : 'Log In'}
                            </button>
                        </nav>
                    </div>
                </div>
            )}
        </header>
    );
}